import React, { useMemo, useState } from 'react';
import {
  Table, Tag, Space, Button, Card, Form, Input, Select, Typography,
  Drawer, DatePicker, Switch, Segmented, Empty, Alert, message, Popconfirm, Tooltip
} from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import ServiceGantt from './ServiceGantt';
import { usePersistentState } from '../../../../utils/usePersistentState';
import { addPlatformLog, buildDetail, OP_TYPE } from '../../../../utils/auditLogStore';

const { Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

// ══════════════════════════════════════════════
//  常量
// ══════════════════════════════════════════════

const STATUS_MAP = {
  pending: { label: '待开始', color: 'default', dot: '#d9d9d9' },
  processing: { label: '进行中', color: 'blue', dot: '#1677ff' },
  completed: { label: '已完成', color: 'green', dot: '#52c41a' },
};

const MILESTONE_DOT = '#fa8c16';

// 操作日志字段标签
const LOG_LABELS = {
  content: '服务内容',
  start: '开始时间',
  end: '结束时间',
  ownerName: '责任人',
  status: '状态',
  milestone: '里程碑',
  remark: '备注',
};

// 转成操作日志展示字段（状态等转成文案）
const toLogView = (r) => ({
  content: r.content,
  start: r.start,
  end: r.end,
  ownerName: r.ownerName,
  status: STATUS_MAP[r.status]?.label || r.status,
  milestone: r.milestone ? '是' : '否',
  remark: r.remark,
});

// 责任人（内部账号）
// TODO: 联调时替换为接口数据
const MOCK_INTERNAL_USERS = [
  { userId: 'user-01', name: '李客服', account: 'lifuwu' },
  { userId: 'user-02', name: '王客服', account: 'wangyuan' },
  { userId: 'user-04', name: '赵主管', account: 'zhaozg' },
  { userId: 'user-05', name: '王主管', account: 'wangzg' },
];

const RANGE_OPTIONS = [
  { label: '近1月', value: '1m' },
  { label: '近3月', value: '3m' },
  { label: '近6月', value: '6m' },
];

// TODO: 联调时替换为接口数据，按 enterprise_id 查询
const MOCK_SCHEDULES = [
  { key: 'p1', content: 'SDK对接支持', start: '2026-07-01', end: '2026-07-20', ownerId: 'user-01', ownerName: '李客服', status: 'completed', milestone: false, remark: '设备端SDK联调' },
  { key: 'p2', content: '固件兼容性验证', start: '2026-07-21', end: '2026-08-05', ownerId: 'user-02', ownerName: '王客服', status: 'completed', milestone: false, remark: '' },
  { key: 'p3', content: '第一阶段验收', start: '2026-08-06', end: '2026-08-06', ownerId: 'user-01', ownerName: '李客服', status: 'completed', milestone: true, remark: '关键里程碑：一期验收通过' },
  { key: 'p4', content: '批量管理API联调', start: '2026-09-01', end: '2026-09-28', ownerId: 'user-04', ownerName: '赵主管', status: 'processing', milestone: false, remark: '' },
  { key: 'p5', content: '上线部署支持', start: '2026-10-08', end: '2026-10-25', ownerId: 'user-02', ownerName: '王客服', status: 'processing', milestone: false, remark: '配合客户上线' },
  { key: 'p6', content: '二期需求评审', start: '2026-11-05', end: '2026-11-05', ownerId: 'user-04', ownerName: '赵主管', status: 'pending', milestone: true, remark: '里程碑：二期启动评审' },
];

// ══════════════════════════════════════════════
//  主组件：服务排期 Tab
//  mode: platform（可维护）/ enterprise（只读）
// ══════════════════════════════════════════════

const ServiceScheduleTab = ({ mode = 'platform', isArchived }) => {
  const isEnterprise = mode === 'enterprise';
  const editable = !isEnterprise && !isArchived;

  const [editForm] = Form.useForm();
  const [records, setRecords] = usePersistentState('mock_service_schedule', MOCK_SCHEDULES);
  const [view, setView] = useState('gantt');
  const [range, setRange] = useState('6m');
  const [statusFilter, setStatusFilter] = useState();
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [editRecord, setEditRecord] = useState(null);

  const filteredRecords = useMemo(
    () => (statusFilter ? records.filter((r) => r.status === statusFilter) : records),
    [records, statusFilter]
  );

  // 甘特时间范围
  const { xMin, xMax } = useMemo(() => {
    const months = { '1m': 1, '3m': 3, '6m': 6 }[range] || 6;
    return {
      xMin: dayjs().subtract(months, 'month').valueOf(),
      xMax: dayjs().add(1, 'month').valueOf(),
    };
  }, [range]);

  const openAdd = () => {
    setEditRecord(null);
    editForm.resetFields();
    editForm.setFieldsValue({ status: 'pending', milestone: false });
    setDrawerVisible(true);
  };

  const openEdit = (record) => {
    setEditRecord(record);
    editForm.setFieldsValue({
      ...record,
      start: record.start ? dayjs(record.start) : null,
      end: record.end ? dayjs(record.end) : null,
    });
    setDrawerVisible(true);
  };

  // 写入操作日志（服务排期仅平台客服维护 → 平台操作日志）
  const emitLog = (entry) => {
    addPlatformLog({ ...entry, module: '客户跟踪 > 服务排期' });
  };

  const handleSave = () => {
    editForm.validateFields().then((values) => {
      const owner = MOCK_INTERNAL_USERS.find((u) => u.userId === values.ownerId);
      const payload = {
        ...values,
        start: values.start?.format('YYYY-MM-DD') || '',
        end: values.end?.format('YYYY-MM-DD') || '',
        ownerName: owner ? owner.name : '',
      };
      if (editRecord) {
        setRecords((prev) => prev.map((r) => (r.key === editRecord.key ? { ...r, ...payload } : r)));
        const before = toLogView(editRecord);
        const after = toLogView(payload);
        const detail = buildDetail(before, after, LOG_LABELS);
        const changedKeys = detail ? Object.keys(detail.after) : [];
        const isStatusOnly = changedKeys.length === 1 && changedKeys[0] === 'status';
        emitLog({
          opType: isStatusOnly ? OP_TYPE.status : OP_TYPE.update,
          summary: isStatusOnly
            ? `状态变更：${before.status} → ${after.status}`
            : `编辑服务排期：${payload.content}`,
          detail,
        });
        message.success('服务排期已更新');
      } else {
        const newRec = { key: `p${Date.now()}`, ...payload };
        setRecords((prev) => [newRec, ...prev]);
        emitLog({
          opType: OP_TYPE.create,
          summary: `新增服务排期：${payload.content}（${payload.start} ~ ${payload.end}）`,
          detail: buildDetail(null, toLogView(newRec), LOG_LABELS),
        });
        message.success('服务排期已新增');
      }
      setDrawerVisible(false);
      setEditRecord(null);
    }).catch(() => {});
  };

  const handleDelete = (record) => {
    setRecords((prev) => prev.filter((r) => r.key !== record.key));
    emitLog({
      opType: OP_TYPE.delete,
      summary: `删除服务排期：${record.content}`,
      detail: buildDetail(toLogView(record), null, LOG_LABELS),
    });
    message.success('已删除');
  };

  const columns = [
    { title: '服务内容', dataIndex: 'content', key: 'content', width: 200, ellipsis: true },
    { title: '开始时间', dataIndex: 'start', key: 'start', width: 120, sorter: (a, b) => a.start.localeCompare(b.start), defaultSortOrder: 'ascend' },
    { title: '结束时间', dataIndex: 'end', key: 'end', width: 120 },
    {
      title: '责任人', dataIndex: 'ownerName', key: 'ownerName', width: 120,
      render: (v, r) => {
        if (!v) return '-';
        if (isEnterprise) return v;
        const u = MOCK_INTERNAL_USERS.find((x) => x.userId === r.ownerId);
        return (
          <div>
            <div style={{ fontWeight: 500 }}>{v}</div>
            {u && <Text type="secondary" style={{ fontSize: 12 }}>{u.account}</Text>}
          </div>
        );
      },
    },
    {
      title: '状态', dataIndex: 'status', key: 'status', width: 100,
      render: (s) => {
        const m = STATUS_MAP[s];
        return m ? <Tag color={m.color} style={{ border: 'none' }}>{m.label}</Tag> : s;
      },
    },
    {
      title: '里程碑', dataIndex: 'milestone', key: 'milestone', width: 100,
      render: (v) => (v ? <Tag color="orange" style={{ border: 'none' }}>◆ 里程碑</Tag> : '-'),
    },
    {
      title: '备注', dataIndex: 'remark', key: 'remark', width: 200, ellipsis: true,
      render: (v) => (v ? <Tooltip title={v}>{v}</Tooltip> : '-'),
    },
    ...(editable ? [{
      title: '操作', key: 'action', fixed: 'right', width: 120,
      render: (_, record) => (
        <Space size={0} split={<span style={{ color: '#e8e8e8' }}>|</span>}>
          <Button type="link" size="small" onClick={() => openEdit(record)}>编辑</Button>
          <Popconfirm title="确定删除该服务排期？" onConfirm={() => handleDelete(record)}>
            <Button type="link" size="small" danger>删除</Button>
          </Popconfirm>
        </Space>
      ),
    }] : []),
  ];

  return (
    <div>
      {!editable && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message={isEnterprise ? '服务排期由平台客服维护，企业仅可查看' : '客户已归档，本 Tab 只读'}
        />
      )}

      <Card bordered={false} style={{ borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}>
        {/* 工具栏 */}
        <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
          <Space size={12}>
            <Segmented
              value={view}
              onChange={setView}
              options={[{ label: '甘特图', value: 'gantt' }, { label: '列表', value: 'list' }]}
            />
            <Select
              value={statusFilter}
              onChange={setStatusFilter}
              allowClear
              placeholder="状态"
              style={{ width: 120 }}
            >
              {Object.entries(STATUS_MAP).map(([k, v]) => <Option key={k} value={k}>{v.label}</Option>)}
            </Select>
            {view === 'gantt' && (
              <Segmented value={range} onChange={setRange} options={RANGE_OPTIONS} />
            )}
          </Space>
          {editable && <Button type="primary" icon={<PlusOutlined />} onClick={openAdd}>新增服务排期</Button>}
        </div>

        {view === 'gantt' ? (
          filteredRecords.length > 0 ? (
            <>
              <ServiceGantt records={filteredRecords} xMin={xMin} xMax={xMax} />
              <div style={{ marginTop: 12, display: 'flex', gap: 20, flexWrap: 'wrap' }}>
                {Object.entries(STATUS_MAP).map(([k, v]) => (
                  <Space key={k} size={6}>
                    <span style={{ display: 'inline-block', width: 12, height: 12, borderRadius: 2, background: v.dot }} />
                    <Text type="secondary" style={{ fontSize: 12 }}>{v.label}</Text>
                  </Space>
                ))}
                <Space size={6}>
                  <span style={{ display: 'inline-block', width: 10, height: 10, background: MILESTONE_DOT, transform: 'rotate(45deg)' }} />
                  <Text type="secondary" style={{ fontSize: 12 }}>里程碑</Text>
                </Space>
              </div>
            </>
          ) : (
            <Empty description="暂无服务排期" style={{ padding: '80px 0' }} />
          )
        ) : (
          <Table
            columns={columns}
            dataSource={filteredRecords}
            rowKey="key"
            scroll={{ x: 1000 }}
            pagination={{
              showSizeChanger: true,
              showTotal: (total) => `共 ${total} 条记录`,
              defaultPageSize: 10,
              pageSizeOptions: ['10', '20', '50'],
            }}
            locale={{ emptyText: <Empty description="暂无服务排期" /> }}
          />
        )}
      </Card>

      {/* 新增/编辑抽屉 */}
      <Drawer
        title={editRecord ? '编辑服务排期' : '新增服务排期'}
        width={640}
        open={drawerVisible}
        onClose={() => { setDrawerVisible(false); setEditRecord(null); }}
        destroyOnClose
        footer={
          <div style={{ textAlign: 'right' }}>
            <Space>
              <Button onClick={() => { setDrawerVisible(false); setEditRecord(null); }}>取消</Button>
              <Button type="primary" onClick={handleSave}>保存</Button>
            </Space>
          </div>
        }
      >
        <Form form={editForm} layout="vertical">
          <Form.Item name="content" label="服务内容" rules={[{ required: true, message: '请输入服务内容' }]}>
            <Input placeholder="如：第一阶段验收" />
          </Form.Item>
          <Form.Item name="start" label="开始时间" rules={[{ required: true, message: '请选择开始时间' }]}>
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item
            name="end"
            label="结束时间"
            dependencies={['start']}
            rules={[
              { required: true, message: '请选择结束时间' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  const start = getFieldValue('start');
                  if (!value || !start || !value.isBefore(start, 'day')) return Promise.resolve();
                  return Promise.reject(new Error('结束时间不能早于开始时间'));
                },
              }),
            ]}
          >
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="ownerId" label="责任人">
            <Select placeholder="请选择责任人" showSearch optionFilterProp="children" allowClear>
              {MOCK_INTERNAL_USERS.map((u) => (
                <Option key={u.userId} value={u.userId}>{u.name} / {u.account}</Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="status" label="状态" rules={[{ required: true, message: '请选择状态' }]}>
            <Select>
              {Object.entries(STATUS_MAP).map(([k, v]) => <Option key={k} value={k}>{v.label}</Option>)}
            </Select>
          </Form.Item>
          <Form.Item name="milestone" label="里程碑" valuePropName="checked" extra="开启后甘特图将以菱形节点展示该事项">
            <Switch />
          </Form.Item>
          <Form.Item name="remark" label="备注">
            <TextArea rows={3} placeholder="选填" />
          </Form.Item>
        </Form>
      </Drawer>
    </div>
  );
};

export default ServiceScheduleTab;
